import { ChartConfiguration } from 'chart.js'

import configureBarChart from 'components/chart-v2/components/bar'
import configureLineChart from 'components/chart-v2/components/line'
import { ChartType, DataPoint } from 'components/chart-v2/model'

export const chartColors = [
  '#D44C47',
  '#C14C8A',
  '#9065B0',
  '#337EA9',
  '#448361',
  '#CB912F',
  '#D9730D',
  '#9F6B53',
  '#787774',
  '#37352F',
]

// TODO: solve colors on the backend. this is a temporary solution
// Generate a set of maximally distinct colors
const generateDistinctColors = (count: number): string[] => {
  const colors: string[] = []
  const goldenRatio = 0.618033988749895

  for (let i = 0; i < count; i++) {
    const hue = (i * goldenRatio * 360) % 360
    const saturation = 65 + (i % 3) * 15 // Vary between 65%, 80%, 95%
    const lightness = 45 + (i % 2) * 15 // Vary between 45%, 60%
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`)
  }

  return colors
}

// Generate optimal colors for a specific set of categories
export const generateOptimalColors = (
  categories: string[],
): Record<string, string> => {
  const uniqueCategories = [...new Set(categories)] // Remove duplicates
  const distinctColors = generateDistinctColors(uniqueCategories.length)
  const colorMap: Record<string, string> = {}

  uniqueCategories.forEach((category, index) => {
    colorMap[category] = distinctColors[index]!
  })

  return colorMap
}

// Get color for a category from a pre-generated color map
export const getOptimalColor = (
  category: string,
  colorMap: Record<string, string>,
): string => {
  return colorMap[category] || '#999999' // Fallback color
}

// Generate a consistent color based on a string hash
export const getConsistentColor = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Use golden ratio for better hue distribution
  const goldenRatio = 0.618033988749895
  const hue = (Math.abs(hash) * goldenRatio * 360) % 360

  // Vary saturation and lightness for more distinction
  const saturation = 70 + (Math.abs(hash) % 25) // 70-95%
  const lightness = 40 + (Math.abs(hash) % 20) // 40-60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

// Extended color palette for fallback
export const extendedChartColors = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#FF6384',
  '#C9CBCF',
  '#4BC0C0',
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#E74C3C',
  '#3498DB',
  '#F39C12',
  '#2ECC71',
  '#9B59B6',
  '#E67E22',
  '#1ABC9C',
  '#34495E',
  '#F1C40F',
  '#E91E63',
  '#673AB7',
  '#FF5722',
  '#795548',
  '#607D8B',
  '#8BC34A',
  '#CDDC39',
  '#FFC107',
  '#FF9800',
  '#00BCD4',
  '#009688',
  '#4CAF50',
  '#8BC34A',
  '#CDDC39',
  '#FFEB3B',
  '#FFC107',
  '#FF9800',
  '#FF5722',
  '#F44336',
  '#E91E63',
  '#9C27B0',
  '#673AB7',
  '#3F51B5',
  '#2196F3',
  '#03A9F4',
  '#00BCD4',
  '#009688',
]

// Chart.js configuration interpreter
export const createChartConfig = (
  data: DataPoint[],
  chartType: ChartType,
  chartOptions?: Record<string, unknown>,
  colorMap?: Record<string, string>,
): ChartConfiguration => {
  const labels = data.map((item) => item.category[0] ?? '')
  const values = data.map((item) => item.values[0] ?? 0)

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Chart Data',
      },
    },
  }

  const type = chartType || 'bar'

  switch (type) {
    case 'bar':
      return configureBarChart(
        data,
        chartOptions as Parameters<typeof configureBarChart>[1],
        colorMap ?? {},
      ) as unknown as ChartConfiguration

    case 'line':
      return configureLineChart(
        data,
        chartOptions as Parameters<typeof configureLineChart>[1],
        colorMap ?? {},
      ) as unknown as ChartConfiguration

    case 'pie':
      return {
        type: 'pie',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF',
                '#FF9F40',
                '#FF6384',
                '#C9CBCF',
                '#4BC0C0',
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
              ],
            },
          ],
        },
        options: baseOptions,
      }

    case 'area':
      return {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Values',
              data: values,
              borderColor: 'rgba(153, 102, 255, 1)',
              backgroundColor: 'rgba(153, 102, 255, 0.3)',
              fill: true,
              tension: 0.4,
            },
          ],
        },
        options: {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      }

    default:
      throw new Error(`Unsupported chart type: ${chartType}`)
  }
}
